import {
  CsvExport,
  ExcelExport,
  ExcelSheet,
} from '../models/api/core/export/export';

export class LeaveExportMapper {
  // Define allowed columns in the exact order for export
  private static readonly ALLOWED_COLUMNS = [
    'employeeName',
    'absenceNumber',
    'date',
    'type',
    'status',
    'hoursAbsent',
    'absenceReason',
  ];

  static toCsvExport(rows: any[], columns: any[], fileName: string): CsvExport {
    const filteredColumns = this.filterAndOrderColumns(columns);
    const headers = filteredColumns.map(this.getHeaderText);
    const dataRows = rows.map((row) =>
      filteredColumns.map((col) => this.getColumnValue(row, col)),
    );

    return {
      fileName,
      headers,
      rows: dataRows,
    };
  }

  static toExcelExport(
    rows: any[],
    columns: any[],
    fileName: string,
    sheetLabel: string,
  ): ExcelExport {
    const filteredColumns = this.filterAndOrderColumns(columns);
    const headers = filteredColumns.map(this.getHeaderText);
    const dataRows = rows.map((row) =>
      filteredColumns.map((col) => this.getColumnValue(row, col)),
    );

    const sheet: ExcelSheet = {
      label: sheetLabel,
      headers,
      rows: dataRows,
    };

    return {
      fileName,
      sheets: [sheet],
    };
  }

  private static filterAndOrderColumns(columns: any[]): any[] {
    // Filter to only include allowed columns and sort by the defined order
    return columns
      .filter((col) => this.ALLOWED_COLUMNS.includes(col.propertyName))
      .sort((a, b) => {
        const indexA = this.ALLOWED_COLUMNS.indexOf(a.propertyName);
        const indexB = this.ALLOWED_COLUMNS.indexOf(b.propertyName);
        return indexA - indexB;
      });
  }

  private static getHeaderText(column: any): string {
    const headerMappings: Record<string, string> = {
      employeeName: 'Name',
      absenceNumber: 'Claim Number',
      date: 'Date of Absence',
      type: 'Type',
      hoursAbsent: 'Hours Absent',
      status: 'Status',
      absenceReason: 'Reason',
    };

    return (
      headerMappings[column.propertyName] ||
      column.headerText ||
      column.propertyName
    );
  }

  private static getFieldValue(row: any, propertyName: string): any {
    const keys = Object.keys(row ?? {});
    const lowerKeyMap = keys.reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      },
      {} as Record<string, string>,
    );

    const actualKey = lowerKeyMap[propertyName.toLowerCase()];
    return actualKey ? row[actualKey] : undefined;
  }

  private static getColumnValue(row: any, column: any): any {
    if (!row || !column) return undefined;
    const propertyName = column.propertyName;
    const keys = Object.keys(row);
    const lowerKeyMap = keys.reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      },
      {} as Record<string, string>,
    );

    const actualKey = lowerKeyMap[propertyName.toLowerCase()];
    const value = actualKey ? row[actualKey] : undefined;

    // Format hours absent as 'X hrs Y minutes'
    if (propertyName === 'hoursAbsent' && value != null) {
      const hoursValue =
        typeof value === 'number'
          ? value
          : parseFloat(String(value).replace(/,/g, ''));
      const minutesValue = this.getFieldValue(row, 'minutesAbsent');
      const minutes =
        minutesValue != null && minutesValue !== ''
          ? Number.parseFloat(String(minutesValue).replace(/,/g, ''))
          : null;

      if (!Number.isNaN(hoursValue)) {
        const baseMinutes =
          minutes != null && !Number.isNaN(minutes)
            ? hoursValue * 60 + minutes
            : hoursValue * 60;

        const totalMinutes = Math.round(baseMinutes);
        const wholeHours = Math.floor(totalMinutes / 60);
        const remainingMinutes = totalMinutes % 60;
        return `${wholeHours} hrs ${remainingMinutes} minutes`;
      }
      return value;
    }

    if (column.format === 'currency' && value != null) {
      const num =
        typeof value === 'number'
          ? value
          : parseFloat(value.toString().replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) {
        return num;
      }
      return value;
    }

    // Format date
    if (column.format === 'date' && value) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
    }

    return value;
  }
}
